import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAdminAlerts,
  fetchAdminStats,
  fetchAdminTenants,
  startImpersonation,
} from "../services/api";
import { parseServerDate } from "../utils/time";
import { fmtEur, fmtRating, pct } from "../components/admin/formatters";
import StatCard from "../components/admin/StatCard";
import AlertRow from "../components/admin/AlertRow";
import TierBar from "../components/admin/TierBar";
import TenantTable from "../components/admin/TenantTable";

/**
 * Founder HQ dashboard. Visible only to operators whose email is in
 * ADMIN_EMAILS (server-side check is the source of truth — the frontend
 * gate is just to hide the nav link).
 *
 * Day 1 layout:
 *   1. "Needs your attention" alert strip (red flags first)
 *   2. Operational KPI tiles (active today, silent, ratings, open tabs)
 *   3. Plan distribution + MRR estimate
 *   4. Tenants table with search + filter (Day 2 will add health columns)
 */

const PAGE_SIZE = 25;

// ----- Main ----------------------------------------------------------
export default function AdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], counts: {} });
  const [tenants, setTenants] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [sort, setSort] = useState("needs_attention");
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleLoginAs = async (tenant) => {
    if (!window.confirm(
      `Login as ${tenant.name}? Your admin session is saved and you can exit any time.`
    )) return;
    setImpersonatingId(tenant.id);
    try {
      await startImpersonation(tenant.id);
      window.location.href = "/";
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start impersonation.");
      setImpersonatingId(null);
    }
  };

  // Stats + alerts reload independently of the tenants table filters.
  useEffect(() => {
    let active = true;
    Promise.all([fetchAdminStats(), fetchAdminAlerts()])
      .then(([s, a]) => {
        if (!active) return;
        setStats(s);
        setAlerts(a);
        setError(null);
      })
      .catch((e) => {
        if (!active) return;
        setError(e?.response?.data?.detail || "Failed to load admin data.");
      });
    return () => { active = false; };
  }, []);

  // Tenants list reloads when filters change.
  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAdminTenants({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      q: q.trim() || undefined,
      tier: tier || undefined,
      sort,
    })
      .then((t) => active && setTenants(t))
      .catch((e) => active && setError(e?.response?.data?.detail || "Failed to load tenants."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [page, tier, q, sort]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((tenants.total || 0) / PAGE_SIZE)),
    [tenants.total]
  );

  // Day-over-day order delta for the orders-today tile sub
  const orderDeltaText = useMemo(() => {
    if (!stats || stats.orders_yesterday == null) return null;
    const t = stats.orders_today ?? 0;
    const y = stats.orders_yesterday ?? 0;
    if (y === 0) return t > 0 ? `+${t} from zero yesterday` : "no orders yesterday";
    const delta = t - y;
    const sign = delta >= 0 ? "+" : "";
    const pctChg = Math.round((delta / y) * 100);
    return `${sign}${delta} vs yesterday (${sign}${pctChg}%)`;
  }, [stats]);

  return (
    <>
      <div className="space-between" style={{ marginBottom: 16 }}>
        <h1 style={{ marginBottom: 0 }}>Founder HQ</h1>
        <span className="muted">
          {stats?.as_of && `Updated ${parseServerDate(stats.as_of).toLocaleTimeString()}`}
        </span>
      </div>

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ------------ ALERT STRIP ------------ */}
      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <div className="space-between" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>
            Needs your attention
            {alerts.counts.total > 0 && (
              <span
                style={{
                  marginLeft: 10,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "#5c7c7f",
                }}
              >
                {alerts.counts.red || 0} critical · {alerts.counts.yellow || 0} warning · {alerts.counts.green || 0} action
              </span>
            )}
          </h3>
        </div>
        {alerts.alerts.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            🎉 Nothing flagged. Every active tenant is healthy.
          </p>
        ) : (
          alerts.alerts.map((a, i) => (
            <AlertRow key={`${a.kind}-${a.restaurant_id}-${i}`} alert={a} />
          ))
        )}
      </div>

      {/* ------------ KPI TILES ------------ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <StatCard
          label="Active today"
          value={stats ? `${stats.active_today ?? 0} / ${stats.total_tenants ?? 0}` : null}
          sub={
            stats?.total_tenants
              ? `${pct(stats.active_today || 0, stats.total_tenants)}% of all tenants`
              : null
          }
          tone="good"
        />
        <StatCard
          label="Silent ≥ 7 days"
          value={stats?.silent_7d}
          sub={(stats?.silent_7d || 0) > 0 ? "see alert strip above" : "all caught up"}
          tone={(stats?.silent_7d || 0) > 0 ? "bad" : "good"}
        />
        <StatCard
          label="Orders today"
          value={stats?.orders_today}
          sub={orderDeltaText}
          tone={
            stats && stats.orders_today > (stats.orders_yesterday || 0)
              ? "good"
              : stats && stats.orders_today < (stats.orders_yesterday || 0)
              ? "warn"
              : undefined
          }
        />
        <StatCard
          label="Avg food rating · 7d"
          value={fmtRating(stats?.avg_food_rating_7d)}
          sub={
            stats?.rating_count_7d
              ? `from ${stats.rating_count_7d} reviews`
              : "no ratings yet"
          }
          tone={
            stats?.avg_food_rating_7d == null ? undefined :
            stats.avg_food_rating_7d >= 4.0 ? "good" :
            stats.avg_food_rating_7d >= 3.5 ? "warn" : "bad"
          }
        />
        <StatCard
          label="Avg service rating · 7d"
          value={fmtRating(stats?.avg_service_rating_7d)}
          sub={stats?.rating_count_7d ? `from ${stats.rating_count_7d} reviews` : "no ratings yet"}
          tone={
            stats?.avg_service_rating_7d == null ? undefined :
            stats.avg_service_rating_7d >= 4.0 ? "good" :
            stats.avg_service_rating_7d >= 3.5 ? "warn" : "bad"
          }
        />
        <StatCard
          label="Open orders · platform"
          value={stats?.open_tabs_total}
          sub={(stats?.open_tabs_total || 0) > 0 ? "live across all hotels" : "everything closed"}
        />
        <StatCard
          label="Pilots ending · 14d"
          value={stats?.pilots_ending_14d}
          sub={(stats?.pilots_ending_14d || 0) > 0 ? "time to convert" : "none coming due"}
          tone={(stats?.pilots_ending_14d || 0) > 0 ? "warn" : undefined}
        />
        <StatCard
          label="Churn risk"
          value={stats?.churn_risk_count}
          sub={(stats?.churn_risk_count || 0) > 0 ? "paid + silent ≥14d" : "no risk flagged"}
          tone={(stats?.churn_risk_count || 0) > 0 ? "warn" : "good"}
        />
      </div>

      {/* ------------ SECONDARY KPI ROW (signups + MRR + plans) ------------ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <StatCard
          label="Total tenants"
          value={stats?.total_tenants}
          sub={stats ? `${stats.new_tenants_7d} new this week` : null}
        />
        <StatCard
          label="Paid tenants"
          value={stats?.paid_tenants}
          sub={
            stats
              ? (() => {
                  const c = {};
                  Object.entries(stats.tier_counts || {}).forEach(([k, v]) => {
                    const key = String(k).toLowerCase();
                    c[key] = (c[key] || 0) + (Number(v) || 0);
                  });
                  const g = (...a) => a.reduce((s, x) => s + (c[x] || 0), 0);
                  const parts = [
                    [g("starter"), "Starter"],
                    [g("pro", "professional"), "Professional"],
                    [g("business", "hotel"), "Hotel"],
                    [g("enterprise", "enterprise+"), "Enterprise"],
                  ].filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`);
                  return parts.length ? parts.join(" · ") : "No paid tenants yet";
                })()
              : null
          }
        />
        <StatCard
          label="MRR estimate"
          value={stats ? fmtEur(stats.mrr_eur_estimate) : null}
          sub="Stripe is the source of truth"
        />
        <StatCard
          label="Orders · 7d"
          value={stats?.orders_7d}
          sub={stats ? `${stats.total_orders} all-time` : null}
        />
      </div>

      {/* ------------ PLAN DISTRIBUTION ------------ */}
      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Plan distribution</h3>
        {stats && <TierBar counts={stats.tier_counts || {}} total={stats.total_tenants || 0} />}
      </div>

      {/* ------------ TENANTS HEALTH TABLE ------------ */}
      <TenantTable
        tenants={tenants}
        loading={loading}
        q={q}
        setQ={setQ}
        tier={tier}
        setTier={setTier}
        sort={sort}
        setSort={setSort}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        onView={(t) => navigate(`/admin/tenants/${t.id}`)}
        onLoginAs={handleLoginAs}
        impersonatingId={impersonatingId}
      />
    </>
  );
}
